import { trackCTA, trackEvent } from './ga';

/**
 * Finds section context by walking up the DOM tree looking for section data attributes.
 * These attributes are added by SectionBackgroundRenderer.
 */
function findSectionContext(element: HTMLElement): { sectionKey?: string; sectionIndex?: number } {
  let current: HTMLElement | null = element;

  while (current) {
    const sectionName = current.getAttribute('data-section-background');
    const sectionIndexAttr = current.getAttribute('data-section-index');

    if (sectionName || sectionIndexAttr) {
      return {
        sectionKey: sectionName || undefined,
        sectionIndex: sectionIndexAttr ? parseInt(sectionIndexAttr, 10) : undefined,
      };
    }

    current = current.parentElement;
  }

  return {};
}

/**
 * Extracts clean text from an element for tracking purposes.
 */
function extractText(element: HTMLElement): string {
  // First check for explicit tracking text
  const trackText = element.getAttribute('data-track-text');
  if (trackText?.trim()) return trackText.trim();

  // Check aria-label
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel?.trim()) return ariaLabel.trim();

  // Check title attribute
  const title = element.getAttribute('title');
  if (title?.trim()) return title.trim();

  // Fall back to visible text content (cleaned up)
  const textContent = element.innerText || element.textContent || '';
  return textContent.replace(/\s+/g, ' ').trim().slice(0, 100);
}

let initialized = false;

/**
 * Initializes a global click event listener that tracks clicks on elements
 * with `data-track-id` attributes.
 *
 * This is a non-invasive way to add analytics tracking to buttons without
 * modifying their existing onClick handlers or visual design.
 *
 * Supported data attributes:
 * - `data-track-id` (required): Unique identifier for the element
 * - `data-track-type`: Element type (button, cta, toggle, filter, link, nav). Default: 'button'
 * - `data-track-text`: Override for element text (uses innerText if not provided)
 * - `data-track-value`: Current value for toggles/filters
 * - `data-track-dest-type`: Destination type (scroll, link, external, download, email, phone)
 * - `data-track-dest-value`: Destination value (URL, anchor, etc.)
 */
export function initGlobalClickTracker() {
  // Prevent double initialization
  if (initialized) return;

  // Only run in browser environment
  if (typeof document === 'undefined') return;

  initialized = true;

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    // Find the closest element with tracking attributes
    const trackableEl = target.closest('[data-track-id]') as HTMLElement | null;

    if (!trackableEl) return;

    // Extract tracking data from attributes
    const trackId = trackableEl.getAttribute('data-track-id');
    if (!trackId) return;

    const trackType = trackableEl.getAttribute('data-track-type') || 'button';
    const trackText = extractText(trackableEl);
    const trackValue = trackableEl.getAttribute('data-track-value') || undefined;
    const destType = trackableEl.getAttribute('data-track-dest-type') || undefined;
    const destValue = trackableEl.getAttribute('data-track-dest-value') || undefined;

    // Get section context from DOM
    const section = findSectionContext(trackableEl);

    // For CTA-type elements, use trackCTA for conversion tracking
    if (trackType === 'cta') {
      trackCTA({
        cta_id: trackId,
        cta_text: trackText || undefined,
        destination_type: destType,
        destination_value: destValue || trackValue,
        section_key: section.sectionKey,
        section_index: section.sectionIndex,
      });
    } else {
      // For other elements (toggles, filters, nav), use generic trackEvent
      trackEvent('click_element', {
        element_type: trackType,
        element_id: trackId,
        element_text: trackText || undefined,
        element_value: trackValue,
        destination_type: destType,
        destination_value: destValue,
        section_key: section.sectionKey,
        section_index: section.sectionIndex,
      });
    }
  }, { capture: true });
}
